import {
  Box,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { FiberManualRecord } from '@mui/icons-material';
import { Field, FieldProps, useFormikContext } from 'formik';
import {
  GreenLightValue,
  GreenLightDefinitionType,
  LightColor,
  Terminal,
} from 'modules/global/adapters/crpAPI';
import FormikSelect from 'modules/global/components/FormikSelect/FormikSelect';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from 'store/actions/messageActions';
import store from 'store/store';
import { LightColor as Color } from 'theme/constants';
import useStyles from './styles';
import IdentificationSelectAutoComplete from 'modules/global/components/IdentificationSelectAutoComplete/IdentificationSelectAutoComplete';
import { LABEL } from 'theme/constants';
import { Autocomplete } from '@mui/material';

type Props = {
  terminals: Terminal[] | undefined;
  greenLightValues: GreenLightValue[] | undefined;
  isLoadingError: boolean;
  isFetching: boolean;
  isValid: boolean;
};

const TerminalSearchStep = ({
  terminals,
  greenLightValues,
  isLoadingError,
  isValid,
  isFetching,
}: Props) => {
  const { t } = useTranslation(['glossary', 'useCase']);
  const { classes } = useStyles();
  const { setFieldValue } = useFormikContext();

  React.useEffect(() => {
    isLoadingError && store.dispatch(errorMessage(t('messages.errorGetAssignPickUpFilterData')));
  }, [isLoadingError, t]);

  React.useEffect(() => {
    setFieldValue('identificationCode', '');
  }, [setFieldValue]);

  const values = greenLightValues === undefined ? [] : greenLightValues;

  const terminalDischargeLights = values
    .filter(
      (gv) =>
        gv.greenLightName === GreenLightDefinitionType.TerminalOperation &&
        gv.value?.toLowerCase() !== 'unknown'
    )
    .sort((a, b) =>
      a.color!.toLowerCase() < b.color!.toLowerCase()
        ? -1
        : b.color!.toLowerCase() > a.color!.toLowerCase()
        ? 1
        : 0
    );
  const terminalReleaseLights = values
    .filter(
      (gv) =>
        gv.greenLightName === GreenLightDefinitionType.TerminalReady &&
        gv.value?.toLowerCase() !== 'unknown'
    )
    .sort((a, b) =>
      a.color!.toLowerCase() < b.color!.toLowerCase()
        ? -1
        : b.color!.toLowerCase() > a.color!.toLowerCase()
        ? 1
        : 0
    );
  const customLights = values
    .filter((gv) => gv.greenLightName === GreenLightDefinitionType.Customs)
    .sort((a, b) =>
      a.color!.toLowerCase() < b.color!.toLowerCase()
        ? -1
        : b.color!.toLowerCase() > a.color!.toLowerCase()
        ? 1
        : 0
    );

  const showIcon = (color: LightColor | undefined) => {
    switch (color) {
      case Color.Grey:
        return <FiberManualRecord fontSize="medium" htmlColor={'#808080'} />;
      case Color.Yellow:
        return <FiberManualRecord fontSize="medium" htmlColor={'#dfc920'} />;
      case Color.Orange:
        return <FiberManualRecord fontSize="medium" htmlColor={'#f57b00'} />;
      case Color.Red:
        return <FiberManualRecord fontSize="medium" htmlColor={'#e60000'} />;
      case Color.Green:
        return <FiberManualRecord fontSize="medium" htmlColor={'#4AB55F'} />;

      case undefined:
      default:
        return <FiberManualRecord fontSize="medium" htmlColor={'#808080'} />;
    }
  };

  return (
    <React.Fragment>
      <Box marginTop={2}>
        <Field name="equipmentNumber">
          {({ field, form }: FieldProps) => (
            <>
              <TextField {...field} variant="outlined" label={t('equipmentNumber')} fullWidth />
            </>
          )}
        </Field>
      </Box>
      <Box marginTop={2}>
        <Field name="billOfLadingNumber" id="billOfLadingNumber">
          {({ field, form }: FieldProps) => (
            <React.Fragment>
              <TextField {...field} variant="outlined" label={t('billOfLadingNumber')} fullWidth />
            </React.Fragment>
          )}
        </Field>
      </Box>
      <Box marginTop={2}>
        <IdentificationSelectAutoComplete
          showText={false}
          label={LABEL.RECEIVED_FROM}
          translationKey="transferReleaseRight"
          textLabel={undefined}
          roles={[LABEL.SHIPPING_AGENT, LABEL.TRANSPORT_OPERATOR, LABEL.RELEASE_PARTY]}
        />
      </Box>
      <Box marginTop={2}>
        <Field name="terminalNxtEntityId" id="terminalNxtEntityId">
          {({ field, form }: FieldProps) => (
            <Autocomplete
              id="inpTerminalCode"
              options={terminals || []}
              getOptionLabel={(terminal: Terminal) =>
                `${terminal.name} (${terminal.portLoCode} ${terminal.code})`
              }
              value={terminals?.find((t) => t.nxtEntityId === field.value) || null}
              onChange={(_, value) => {
                form.setFieldValue('terminalNxtEntityId', value ? value.nxtEntityId : '');
              }}
              renderInput={(params) => (
                <TextField {...params} label={LABEL.CHOOSE_TERMINAL} fullWidth variant="outlined" />
              )}
              fullWidth
              getOptionDisabled={() => isFetching}
              disabled={isFetching}
            />
          )}
        </Field>
      </Box>

      {values.length > 0 && (
        <>
          <Box marginTop={2}>
            <Field name="terminalOperationValue" id="terminalOperation">
              {({ field, form }: FieldProps) => (
                <React.Fragment>
                  <InputLabel id="terminalOperation">{t('terminalOperationValue')}</InputLabel>
                  <FormikSelect defaultValue={undefined} form={form} field={field} error={false}>
                    {terminalDischargeLights.map((lightValue: GreenLightValue) => (
                      <MenuItem value={lightValue.value} key={lightValue.value}>
                        <div className={classes.greenLightValueIcon}>
                          <ListItemIcon>{showIcon(lightValue.color)}</ListItemIcon>
                          <ListItemText>{lightValue.value}</ListItemText>
                        </div>
                      </MenuItem>
                    ))}
                    <MenuItem value={undefined}>{<i>{t('showAll')}</i>}</MenuItem>
                  </FormikSelect>
                </React.Fragment>
              )}
            </Field>
          </Box>
          <Box marginTop={2}>
            <Field name="terminalReadyValue" id="terminalReady">
              {({ field, form }: FieldProps) => (
                <React.Fragment>
                  <InputLabel id="terminalReady">{t('terminalReadyValue')}</InputLabel>
                  <FormikSelect defaultValue={undefined} form={form} field={field} error={false}>
                    {terminalReleaseLights.map((lightValue: GreenLightValue) => (
                      <MenuItem value={lightValue.value} key={lightValue.value}>
                        <div className={classes.greenLightValueIcon}>
                          <ListItemIcon>{showIcon(lightValue.color)}</ListItemIcon>
                          <ListItemText>{lightValue.value}</ListItemText>
                        </div>
                      </MenuItem>
                    ))}
                    <MenuItem value={undefined}>{<i>{t('showAll')}</i>}</MenuItem>
                  </FormikSelect>
                </React.Fragment>
              )}
            </Field>
          </Box>
          <Box marginTop={2}>
            <Field name="customsValue" id="customsLight">
              {({ field, form }: FieldProps) => (
                <React.Fragment>
                  <InputLabel id="customsLight">{t('customsLight')}</InputLabel>
                  <FormikSelect defaultValue={undefined} form={form} field={field} error={false}>
                    {customLights.map((lightValue: GreenLightValue) => (
                      <MenuItem value={lightValue.value} key={lightValue.value}>
                        <div className={classes.greenLightValueIcon}>
                          <ListItemIcon>{showIcon(lightValue.color)}</ListItemIcon>
                          <ListItemText>{lightValue.value}</ListItemText>
                        </div>
                      </MenuItem>
                    ))}
                    <MenuItem value={undefined}>{<i>{t('showAll')}</i>}</MenuItem>
                  </FormikSelect>
                </React.Fragment>
              )}
            </Field>
          </Box>
          {!isValid && (
            <Box marginTop={2}>
              <FormHelperText className={classes.errorText}>
                {t('validation:atLeastOneField')}
              </FormHelperText>
            </Box>
          )}
        </>
      )}
    </React.Fragment>
  );
};
export default TerminalSearchStep;
